/**
 * Block 13 PR-G Phase 4A — `useContractDraftStore` (Zustand fast cache).
 *
 * Mirror Block 12 `useQuoteDraftStore` (PR-D-A Phase D5 Step 9). Layer 1 in-memory
 * dictionary, sync lookup — переживает SPA navigation в одной сессии. Layer 2
 * (IDB через `contractDraftIdbSync`) — durable cross-session. Layer 3 wiring
 * (`ContractFormContainer`) — cascade Zustand → IDB → fromQuote seed → blank.
 *
 * **Key strategy:** `draftKey: string` = `contractId` UUID. Один draft на ID.
 * `setDraft` перезаписывает.
 *
 * **No persist middleware:** Zustand persist → localStorage; для ContractDraft
 * (с массивами spec/customerRequisites/executorRequisites) IDB подходит лучше.
 * Этот store — только in-memory.
 *
 * Reference: `features/calculator/quote/state/useQuoteDraftStore.ts`.
 */

import { create } from 'zustand';

import type { ContractFormState } from './contractReducer';

export interface ContractDraftStore {
  /** Map draftKey (contractId UUID) → последний ContractFormState. */
  drafts: Partial<Record<string, ContractFormState>>;
  setDraft: (key: string, state: ContractFormState) => void;
  getDraft: (key: string) => ContractFormState | undefined;
  clearDraft: (key: string) => void;
  clearAll: () => void;
}

export const useContractDraftStore = create<ContractDraftStore>((set, get) => ({
  drafts: {},

  setDraft: (key, state) => set((s) => ({ drafts: { ...s.drafts, [key]: state } })),

  getDraft: (key) => get().drafts[key],

  clearDraft: (key) =>
    set((s) => {
      if (!(key in s.drafts)) return s;
      const next = { ...s.drafts };
      delete next[key];
      return { drafts: next };
    }),

  clearAll: () => set({ drafts: {} }),
}));

/** Тестовый утилит — сброс store. Используется в beforeEach test cleanup. */
export function resetContractDraftStoreForTests(): void {
  useContractDraftStore.setState({ drafts: {} });
}
